import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import { getTranslations, upsertTranslation, deleteTranslationApi, getLanguages, getNamespaces } from '../../api/api';

function Translations() {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language || 'es');
  const [namespace, setNamespace] = useState('app');
  const [languages, setLanguages] = useState(['es', 'en']);
  const [namespaces, setNamespaces] = useState(['app']);
  const [rows, setRows] = useState([]); // [{key, value}]
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const loadMeta = async () => {
    try {
      const [langsRes, nsRes] = await Promise.all([getLanguages(), getNamespaces()]);
      const langs = langsRes.data?.data?.length ? langsRes.data.data : ['es', 'en'];
      const nss = nsRes.data?.data?.length ? nsRes.data.data : ['app'];
      setLanguages(langs);
      setNamespaces(nss);
      if (!langs.includes(language)) setLanguage(langs[0]);
      if (!nss.includes(namespace)) setNamespace(nss[0]);
    } catch (e) {
      // Ignore meta errors; use defaults
    }
  };

  const loadTranslations = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getTranslations({ language, namespace });
      const list = res.data?.data || [];
      setRows(list.map((it) => ({ key: it.key, value: it.value })));
    } catch (e) {
      setError(e.response?.data?.error || 'Error loading translations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
  }, []);

  useEffect(() => {
    loadTranslations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, namespace]);

  const handleSaveRow = async (k, v) => {
    try {
      await upsertTranslation({ namespace, language, key: k, value: v });
      setInfo('Saved');
      setTimeout(() => setInfo(''), 2000);
      await loadTranslations();
    } catch (e) {
      setError(e.response?.data?.error || 'Save error');
    }
  };

  const handleDeleteRow = async (k) => {
    try {
      await deleteTranslationApi({ namespace, language, key: k });
      await loadTranslations();
    } catch (e) {
      setError(e.response?.data?.error || 'Delete error');
    }
  };

  const handleAdd = async () => {
    if (!newKey) return;
    await handleSaveRow(newKey, newValue);
    setNewKey('');
    setNewValue('');
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t('translations.title')}
      </Typography>
      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="lang-label">Lang</InputLabel>
                <Select
                  labelId="lang-label"
                  label="Lang"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                >
                  {languages.map((lng) => (
                    <MenuItem key={lng} value={lng}>{lng.toUpperCase()}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="ns-label">Namespace</InputLabel>
                <Select
                  labelId="ns-label"
                  label="Namespace"
                  value={namespace}
                  onChange={(e) => setNamespace(e.target.value)}
                >
                  {namespaces.map((ns) => (
                    <MenuItem key={ns} value={ns}>{ns}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box mt={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <TextField size="small" label="Key" value={newKey} onChange={(e) => setNewKey(e.target.value)} />
              <TextField size="small" label="Value" value={newValue} onChange={(e) => setNewValue(e.target.value)} fullWidth />
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} disabled={!newKey}>
                {t('common.save')}
              </Button>
            </Stack>
          </Box>

          <Box mt={3}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {info && <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert>}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.key} hover>
                    <TableCell width={280}>{row.key}</TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        defaultValue={row.value}
                        onBlur={(e) => handleSaveRow(row.key, e.target.value)}
                      />
                    </TableCell>
                    <TableCell align="right" width={120}>
                      <IconButton color="primary" onClick={() => handleSaveRow(row.key, row.value)} title="Save">
                        <SaveIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDeleteRow(row.key)} title="Delete">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {loading && <Typography variant="body2" sx={{ mt: 2 }}>{t('common.loading')}</Typography>}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default Translations;
